import { Button } from 'shards-react'
import React from 'react'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { useSelector } from 'react-redux'
import styles from '../styles.module.css'

const FILE_TITLE = 'Предложения по организации набора на вакантные места'
const TOTAL_COLS = 10

const COLORS = {
  headerBg:     'FFFAFAFA',
  orgBg:        'FFFFF5F5',
  orgText:      'FFC0392B',
  classBg:      'FFF0F4FF',
  classText:    'FF0070C0',
  lPrivileged:  'FFFF0000',
  mPrivileged:  'FF96450E',
  border:       'FFD0CCCC',
  white:        'FFFFFFFF',
}

const border = {
  top:    { style: 'thin', color: { argb: COLORS.border } },
  left:   { style: 'thin', color: { argb: COLORS.border } },
  bottom: { style: 'thin', color: { argb: COLORS.border } },
  right:  { style: 'thin', color: { argb: COLORS.border } },
}

const baseFont = { name: 'Calibri', size: 10 }

function applyCell(cell, { bgColor, fontColor, bold = false, italic = false, hAlign = 'center', vAlign = 'middle', wrapText = true } = {}) {
  cell.font = { ...baseFont, bold, italic, color: fontColor ? { argb: fontColor } : undefined }
  cell.alignment = { horizontal: hAlign, vertical: vAlign, wrapText }
  cell.border = border
  if (bgColor) {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } }
  }
}

function mergeAndStyle(ws, startRow, startCol, endRow, endCol, value, styleOpts) {
  const topLeft = ws.getCell(startRow, startCol)
  topLeft.value = value
  applyCell(topLeft, styleOpts)
  if (startRow !== endRow || startCol !== endCol) {
    ws.mergeCells(startRow, startCol, endRow, endCol)
  }
  // Fill border on all merged cells so Excel renders borders correctly
  for (let r = startRow; r <= endRow; r++) {
    for (let c = startCol; c <= endCol; c++) {
      const cell = ws.getCell(r, c)
      cell.border = border
      if (styleOpts?.bgColor) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: styleOpts.bgColor } }
      }
    }
  }
}

export default function Buttons({ visible }) {
  const data = useSelector(state => state.reports.hiringPlan.data)

  const toXlsx = async () => {
    const wb = new ExcelJS.Workbook()
    wb.creator = 'HiringPlan'
    const ws = wb.addWorksheet('Набор', { views: [{ state: 'frozen', ySplit: 3 }] })

    // ── Column widths ───────────────────────────────────────────────
    ws.columns = [
      { width: 5  },  // №
      { width: 32 },  // ФИО
      { width: 14 },  // Рус
      { width: 14 },  // Мат
      { width: 14 },  // Иностр
      { width: 8  },  // ФИЗО
      { width: 12 },  // Балл
      { width: 22 },  // Автор
      { width: 22 },  // Предложение
      { width: 22 },  // Примечание
    ]

    // ── Row 1: title ────────────────────────────────────────────────
    mergeAndStyle(ws, 1, 1, 1, TOTAL_COLS, FILE_TITLE, {
      bgColor: COLORS.white,
      bold: true,
      hAlign: 'center',
    })
    ws.getRow(1).height = 20

    // ── Row 2: header line 1 ────────────────────────────────────────
    const headerStyle = { bgColor: COLORS.headerBg, bold: true, hAlign: 'center' }

    mergeAndStyle(ws, 2, 1, 3, 1, '№',                          headerStyle)
    mergeAndStyle(ws, 2, 2, 3, 2, 'Фамилия, имя, отчество\nкандидата на обучение', { ...headerStyle, hAlign: 'left' })
    mergeAndStyle(ws, 2, 3, 2, 7, 'Результат сдачи ВИ',         headerStyle)
    mergeAndStyle(ws, 2, 8, 3, 8, 'Автор обращения',            headerStyle)
    mergeAndStyle(ws, 2, 9, 3, 9, 'Предложение',                headerStyle)
    mergeAndStyle(ws, 2, 10, 3, 10, 'Примечание',               headerStyle)

    // ── Row 3: header line 2 ────────────────────────────────────────
    const subHeaders = ['Русский язык', 'Математика', 'Иностр. язык', 'ФИЗО', 'Общий балл']
    subHeaders.forEach((label, i) => {
      const cell = ws.getCell(3, 3 + i)
      cell.value = label
      applyCell(cell, headerStyle)
    })

    ws.getRow(2).height = 30
    ws.getRow(3).height = 24

    // ── Data rows ───────────────────────────────────────────────────
    let rowIdx = 4

    if (data) {
      for (const org of data) {
        // org row
        mergeAndStyle(ws, rowIdx, 1, rowIdx, TOTAL_COLS, org.Orgname, {
          bgColor: COLORS.orgBg,
          fontColor: COLORS.orgText,
          bold: true,
          hAlign: 'center',
        })
        ws.getRow(rowIdx).height = 18
        rowIdx++

        for (const cls of org.classes) {
          const vacant = cls.plan - cls.fact
          const classText = `${cls.child_class} класс. Вакантных мест - ${vacant} (утверждено - ${cls.plan}, налицо - ${cls.fact})`
          mergeAndStyle(ws, rowIdx, 1, rowIdx, TOTAL_COLS, classText, {
            bgColor: COLORS.classBg,
            fontColor: COLORS.classText,
            bold: true,
            hAlign: 'center',
          })
          ws.getRow(rowIdx).height = 16
          rowIdx++

          for (let idx = 0; idx < cls.students.length; idx++) {
            const student = cls.students[idx]
            const lPrivileged = student.L === '1' || student.L1 === '1'
            const mPrivileged = student.M === '1'
            const fontColor = lPrivileged ? COLORS.lPrivileged : mPrivileged ? COLORS.mPrivileged : undefined

            const values = [
              idx + 1,
              `${student.child_lastname?.toUpperCase() ?? ''} ${student.child_firstname} ${student.child_midname}`,
              student.Grade_Russian,
              student.Grade_Math,
              student.Grade_Lang,
              student.Grade_PE,
              student.Ball,
              '',
              '',
              '',
            ]

            const row = ws.getRow(rowIdx)
            for (let colIdx = 0; colIdx < values.length; colIdx++) {
              const cell = row.getCell(colIdx + 1)
              cell.value = values[colIdx]
              applyCell(cell, {
                fontColor,
                hAlign: colIdx === 1 ? 'left' : 'center',
              })
            }
            row.height = 16
            rowIdx++
          }
        }
      }
    }

    // ── Save ────────────────────────────────────────────────────────
    const buffer = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${FILE_TITLE}.xlsx`)
  }

  if (!visible) return null

  return (
    <div className={styles.buttonsBlock}>
      <Button theme='success' className={'mb-2'} onClick={toXlsx}>
        Скачать .xlsx
      </Button>
    </div>
  )
}
